import React, { useState, useCallback, useEffect } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Theme } from '@mui/material';
import oilDrop from '../../assets/beam-oildrop-bg-hd2.jpg';
import RegistrationInit from './RegistrationInit';
import RegistrationUncomplete from './RegistrationUncomplete';
import RegistrationUnderReview from './RegistrationUnderReview';
import { useTranslation } from '../../common/i18n/useTranslation';
import { grey as greyColor } from '@mercedes-benz/mui5-theme/build/main/lib/Colors';
import { useLandingPageModeContext } from '../contexts/LandingPageModeProvider';
import { LandingPageMode } from '../../utils/enums';
import { WbAspectRatio, WbGrid, WbGridCol, WbGridRow, WbHeading, WbText } from '@workbench/react';
import RegistrationApproved from './RegistrationApproved';
import { useClientCompanyRegistrationContext } from '../client/companyRegistration/context/ClientCompanyRegistrationContext';
import RegistrationRejected from './RegistrationRejected';
import NewsSection from '../newsSection/NewsSection';
import TasksSection from '../newsSection/taskSection/TasksSection';
import MissingPermission from './MissingPermission';
import { getErrorStatus } from '../../common/rest/restOperations';
import { CompanyRegistrationDto } from '../../api';

const useStyles = makeStyles()((theme: Theme) => ({
    greyColor: {
        color: greyColor[95],
    },
    spacing: {
        marginBottom: theme.spacing(5),
    },
    tightSpacing: {
        marginBottom: theme.spacing(1.5),
    },
    fullAccess: {
        paddingTop: theme.spacing(5),
        backgroundColor: greyColor[95],
    },
    lightAccess: {
        paddingTop: theme.spacing(5),
        color: greyColor[95],
    },
    centralizeVertically: {
        alignContent: 'center',
        alignItems: 'center',
        height: '100%',
    },
}));

/**
 * Main component.
 */
function Main() {
    const { classes } = useStyles();
    const translate = useTranslation();
    const { mode, setMode } = useLandingPageModeContext();
    const { registration, userInfo, isAdmin, isCompanyRegistrationDataFetching } =
        useClientCompanyRegistrationContext();
    const [fullAccess, setFullAccess] = useState<boolean>(false);

    const renderContent = useCallback(() => {
        switch (mode) {
            case LandingPageMode.UNCOMPLETE:
                return <RegistrationUncomplete />;
            case LandingPageMode.UNDER_REVIEW:
                return <RegistrationUnderReview />;
            case LandingPageMode.APPROVED:
                return <RegistrationApproved />;
            case LandingPageMode.REJECTED:
                return <RegistrationRejected />;
            case LandingPageMode.MISSING_PERMISSION:
                return <MissingPermission />;
            case LandingPageMode.UNEXPECTED_ERROR:
                return;
            case LandingPageMode.INIT:
            default:
                return <RegistrationInit />;
        }
    }, [mode, userInfo]);

    useEffect(() => {
        if (!userInfo?.userId) {
            const errorStatus = getErrorStatus();
            if (errorStatus === 403) {
                setMode(LandingPageMode.MISSING_PERMISSION);
            } else {
                setMode(LandingPageMode.INIT);
            }
        } else if (registration) {
            switch (registration?.registrationState) {
                case CompanyRegistrationDto.registrationState.IN_PROGRESS:
                case CompanyRegistrationDto.registrationState.IN_APPROVAL:
                    setMode(LandingPageMode.UNDER_REVIEW);
                    return;
                case CompanyRegistrationDto.registrationState.APPROVED:
                    if (registration?.termsOfUseApproved === true) {
                        setMode(LandingPageMode.APPROVED);
                        setFullAccess(true);
                    }
                    return;
                case CompanyRegistrationDto.registrationState.REJECTED:
                    setMode(LandingPageMode.REJECTED);
                    return;
                case CompanyRegistrationDto.registrationState.PENDING:
                default:
                    setMode(LandingPageMode.UNCOMPLETE);
                    return;
            }
        } else if (!isAdmin && !isCompanyRegistrationDataFetching) {
            setMode(LandingPageMode.UNEXPECTED_ERROR);
        }
    }, [registration, setMode, userInfo, mode, registration?.termsOfUseApproved]);

    return (
        <>
            <WbAspectRatio ratio="4x1">
                <img src={oilDrop} alt="oil drop header" />
                <WbGrid className={classes.centralizeVertically}>
                    <WbGridRow className={classes.centralizeVertically}>
                        <WbGridCol mq1="12" className={classes.centralizeVertically}>
                            <WbHeading size="l" className={classes.greyColor}>
                                Mercedes-Benz
                            </WbHeading>
                        </WbGridCol>
                        <WbGridCol mq1="12" className={classes.centralizeVertically}>
                            <WbHeading size="l" className={classes.greyColor}>
                                {`Betriebsstoff Applikations Modul - "BEAM"`}
                            </WbHeading>
                        </WbGridCol>
                    </WbGridRow>
                </WbGrid>
            </WbAspectRatio>
            <div className={fullAccess ? classes.fullAccess : classes.lightAccess}>
                <WbGrid>
                    <WbGridRow>
                        <WbGridCol mq1="8">
                            {fullAccess ? (
                                <>
                                    <WbHeading size="l" className={classes.spacing}>
                                        {translate('welcome_to_beam_full_access', {
                                            name: `${userInfo?.givenName} ${userInfo?.familyName}`,
                                        })}
                                    </WbHeading>
                                    <WbText size="m" className={classes.tightSpacing}>
                                        {translate('welcome_full_access_logged_in_userid', {
                                            userId: userInfo?.userId,
                                        })}
                                    </WbText>
                                    <WbText size="m" className={classes.tightSpacing}>
                                        {translate('welcome_full_access')}
                                    </WbText>
                                </>
                            ) : (
                                <WbHeading size="m" className={classes.spacing}>
                                    {translate('welcome')}
                                </WbHeading>
                            )}
                        </WbGridCol>
                        {fullAccess && (
                            <WbGridCol mq1="4">
                                <TasksSection />
                            </WbGridCol>
                        )}
                    </WbGridRow>
                    <WbGridRow>
                        <WbGridCol mq1="8">{renderContent()}</WbGridCol>
                    </WbGridRow>
                    {fullAccess && (
                        <WbGridRow>
                            <WbGridCol mq1={12}>
                                <NewsSection />
                            </WbGridCol>
                        </WbGridRow>
                    )}
                </WbGrid>
            </div>
        </>
    );
}

export default React.memo(Main);
